import { DrawCustomMode, DrawCustomModeThis, MapMouseEvent } from "@mapbox/mapbox-gl-draw";
import { GeoJSON } from "geojson";

import { PathGraph } from "fond/draw/modes/base/pathGraph";

import { createAssignmentCoordinates, createPreviewLine, getFeatureAtPoint, getFeaturesAtPoint, getOriginalCable, PREVIEW_LINE_ID } from "./helper";
import { AssignmentCableFeature, CableFeature, SourceFeature } from "./types";

interface DrawAssignmentState {
  // The cable/assignment being reassigned
  selectedCable: CableFeature | AssignmentCableFeature;

  // The original cable underlying the selected cable
  originalCable: CableFeature;

  // The underground path graph
  graph: PathGraph;

  // Whether the source selection popup is open
  popupOpen: boolean;
}

const SOURCE_BUFFER_SIZE: number = 15;

interface Options {
  selectedCable: CableFeature | AssignmentCableFeature;
}

type DrawAssignment = DrawCustomMode<DrawAssignmentState, Options> & {
  findSourceAtEvent(this: DrawCustomModeThis, event: MapMouseEvent): SourceFeature | undefined;
};

/**
 * Custom draw mode for cable reassignment with interactive line preview.
 *
 * This mode shows a preview line from the mouse cursor to the service location. The user can click
 * on a source to complete the reassignment.
 */
const drawAssignment: DrawAssignment = {
  /**
   * Initialize the draw mode, creating the 'preview line'.
   *
   * @param selectedCable The cable/assignment that the user clicked.
   */
  onSetup: function ({ selectedCable }): DrawAssignmentState {
    const line = this.newFeature(createPreviewLine(selectedCable));
    this.addFeature(line);

    const graph = this._ctx.options.shortestPath?.graph;
    if (!graph) {
      throw new Error("Path graph not available");
    }

    return {
      selectedCable,
      originalCable: getOriginalCable(selectedCable),
      graph,
      popupOpen: false,
    };
  },

  toDisplayFeatures: function (state: DrawAssignmentState, geojson: GeoJSON, display: (geojson: GeoJSON) => void) {
    if (geojson.type === "Feature" && geojson.properties) {
      if (geojson.properties.id === state.selectedCable.id) {
        geojson.properties.hidden = true;
      }
      geojson.properties.draw_mode = "drawAssignment";
    }
    display(geojson);
  },

  /**
   * Find a source feature at the given event location.
   * @param event The mouse event
   * @returns The source feature if found, undefined otherwise
   */
  findSourceAtEvent: function (this: DrawCustomModeThis, event: MapMouseEvent): SourceFeature | undefined {
    return getFeatureAtPoint(this, event.point, SOURCE_BUFFER_SIZE, ["source"]);
  },

  /**
   * Update the preview line's end point as the mouse moves. If the mouse is near a source,
   * then snap to that source.
   */
  onMouseMove: function (state: DrawAssignmentState, event: MapMouseEvent) {
    // Don't update preview line while source selection popup is open
    if (state.popupOpen) return;

    const previewLine = this.getFeature(PREVIEW_LINE_ID);
    if (!previewLine || previewLine.type !== "LineString") return;

    const source = this.findSourceAtEvent(event);
    const coordinates = createAssignmentCoordinates({
      originalCable: state.originalCable,
      source,
      mousePosition: event.lngLat.toArray(),
      graph: state.graph,
    });
    previewLine.incomingCoords(coordinates);
    previewLine.setProperty("source", source);
  },

  /**
   * Handle clicks to detect source selection and complete reassignment.
   */
  onClick: function (this, state: DrawAssignmentState, event: MapMouseEvent) {
    const sources = getFeaturesAtPoint(this, event.point, SOURCE_BUFFER_SIZE, ["source"]);

    if (sources.length === 0) {
      this.changeMode("select_drop");
      return;
    }

    if (sources.length === 1) {
      // Actually creating the assignment is left to an external event handler because of the multi-source flow below.
      this.map.fire("drop_assignments.complete_assignment", {
        originalCable: state.originalCable,
        selectedCable: state.selectedCable,
        source: sources[0],
      });
      return;
    }

    // There are multiple sources near the click - we show a popup allowing the user to select which source they
    // intended.
    const lngLat = this.map.unproject(event.point);
    state.popupOpen = true;
    this.map.fire("drop_assignments.show_source_selection", {
      sources,
      lngLat,
      selectedCable: state.selectedCable,
      originalCable: state.originalCable,
    });
  },

  /**
   * Cancel on Escape key - restore the original cable.
   */
  onKeyUp: function (state: DrawAssignmentState, event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.changeMode("select_drop");
    }
  },

  /**
   * Cleanup the preview line when mode is exited.
   */
  onStop: function (state: DrawAssignmentState) {
    // This isn't 100% reliable: onStop isn't always called when changing to another mode.
    // To avoid this, the `toDisplayFeatures` function adds a `draw_mode` property to each feature
    // indicating the current draw mode. If we're not in _this_ mode, then the preview line won't be shown,
    // so a failure to delete it is not fatal.
    if (this.getFeature(PREVIEW_LINE_ID)) {
      this.deleteFeature(PREVIEW_LINE_ID, { silent: true });
    }
  },
};

export default drawAssignment;
